import React from 'react';
import {
  Box,
  Heading,
  Button,
  Flex,
  Text,
  Spinner,
  useToast,
  FormControl,
  FormLabel,
  Input,
  Select,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Textarea,
  InputGroup,
  InputLeftElement,
  Icon,
} from '@chakra-ui/react';
import { FiPlus, FiSearch } from 'react-icons/fi';
import dayjs from 'dayjs';
import xhr from 'xhr';

const STATUS_MAP = {
  paid: { label: 'Pagado', color: 'green' },
  pending: { label: 'Pendiente', color: 'yellow' },
  cancelled: { label: 'Cancelado', color: 'red' },
};

const PAYMENT_METHODS = [
  'Transferencia bancaria',
  'Efectivo',
  'Nequi',
  'Daviplata',
  'Consignacion',
  'Otro',
];

export default function SchoolPagos() {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [payments, setPayments] = React.useState([]);
  const [students, setStudents] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [creating, setCreating] = React.useState(false);
  const [schoolId, setSchoolId] = React.useState(null);
  const [filterStatus, setFilterStatus] = React.useState('');
  const [search, setSearch] = React.useState('');

  const [newPayment, setNewPayment] = React.useState({
    student: '',
    amount: '',
    payment_date: dayjs().format('YYYY-MM-DD'),
    payment_method: '',
    description: '',
    status: 'paid',
  });

  React.useEffect(() => {
    const init = async () => {
      try {
        const userRes = await xhr('/users/me?populate=school');
        const id = userRes.data?.school?.id;
        if (id) {
          setSchoolId(id);
          await Promise.all([fetchPayments(id), fetchStudents(id)]);
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const fetchPayments = async (id) => {
    try {
      const sId = id || schoolId;
      const res = await xhr(
        `/payment-schools?filters[school][id][$eq]=${sId}&populate=student&sort[0]=payment_date:desc&pagination[pageSize]=200`
      );
      setPayments(res.data.data || []);
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  };

  const fetchStudents = async (id) => {
    try {
      const sId = id || schoolId;
      const res = await xhr(
        `/students?filters[schoolFrom][id][$eq]=${sId}&fields[0]=id&populate=user&pagination[pageSize]=500`
      );
      const data = res.data.data || [];
      setStudents(
        data.map((s) => {
          const attrs = s.attributes || s;
          const studentUser = attrs.user?.data?.attributes || attrs.user;
          return {
            id: s.id,
            name: `${studentUser?.first_name || ''} ${studentUser?.last_name || ''}`.trim() || `Estudiante ${s.id}`,
            email: studentUser?.email || '',
          };
        })
      );
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const handleCreate = async () => {
    if (!newPayment.student || !newPayment.amount || !newPayment.payment_method) {
      toast({
        title: 'Campos requeridos',
        description: 'Estudiante, monto y metodo de pago son obligatorios',
        status: 'warning',
        position: 'bottom',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    try {
      setCreating(true);
      await xhr.post('/payment-schools', {
        data: {
          student: Number(newPayment.student),
          amount: Number(newPayment.amount),
          payment_date: newPayment.payment_date,
          payment_method: newPayment.payment_method,
          description: newPayment.description,
          status: newPayment.status,
          school: schoolId,
        },
      });

      toast({
        title: 'Pago registrado exitosamente',
        status: 'success',
        position: 'bottom',
        duration: 3000,
        isClosable: true,
      });
      setNewPayment({
        student: '',
        amount: '',
        payment_date: dayjs().format('YYYY-MM-DD'),
        payment_method: '',
        description: '',
        status: 'paid',
      });
      onClose();
      fetchPayments();
    } catch (error) {
      const message = error.response?.data?.error?.message || 'Error al registrar pago';
      toast({
        title: 'Error al registrar pago',
        description: message,
        status: 'error',
        position: 'bottom',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCreating(false);
    }
  };

  const filteredPayments = payments.filter((p) => {
    const attrs = p.attributes || p;
    const student = attrs.student?.data || attrs.student;
    const studentUser = student?.attributes?.user?.data?.attributes || student?.user;

    if (filterStatus && attrs.status !== filterStatus) return false;
    if (search) {
      const name = `${studentUser?.first_name || ''} ${studentUser?.last_name || ''}`.toLowerCase();
      if (!name.includes(search.toLowerCase())) return false;
    }
    return true;
  });

  // Calculate total from filtered payments
  const totalFiltered = filteredPayments.reduce((sum, p) => {
    const attrs = p.attributes || p;
    return sum + Number(attrs.amount || 0);
  }, 0);

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="lg" />
      </Box>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg">Pagos</Heading>
          <Text color="gray.500" fontSize="sm" mt={1}>
            {payments.length} pagos registrados
          </Text>
        </Box>
        <Button leftIcon={<FiPlus />} colorScheme="purple" onClick={onOpen}>
          Registrar pago
        </Button>
      </Flex>

      {/* Filters */}
      <Flex mb={5} gap={3} flexWrap="wrap" align="center">
        <InputGroup maxW="280px">
          <InputLeftElement pointerEvents="none">
            <Icon as={FiSearch} color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Buscar por nombre..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            bg="white"
            borderRadius="lg"
            size="md"
          />
        </InputGroup>
        <Select
          placeholder="Todos los estados"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          maxW="180px"
          bg="white"
          borderRadius="lg"
        >
          <option value="paid">Pagado</option>
          <option value="pending">Pendiente</option>
          <option value="cancelled">Cancelado</option>
        </Select>
        {(filterStatus || search) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFilterStatus('');
              setSearch('');
            }}
          >
            Limpiar filtros
          </Button>
        )}
      </Flex>

      {/* Summary */}
      {(filterStatus || search) && (
        <Flex mb={4} gap={4}>
          <Badge colorScheme="blue" px={3} py={1} borderRadius="full" fontSize="sm">
            {filteredPayments.length} resultado{filteredPayments.length !== 1 ? 's' : ''}
          </Badge>
          <Badge colorScheme="green" px={3} py={1} borderRadius="full" fontSize="sm">
            Total: ${totalFiltered.toLocaleString()}
          </Badge>
        </Flex>
      )}

      {/* Table */}
      <Box bg="white" borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.100" overflow="hidden">
        <TableContainer>
          <Table size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th>Fecha</Th>
                <Th>Estudiante</Th>
                <Th isNumeric>Monto</Th>
                <Th>Metodo</Th>
                <Th>Descripcion</Th>
                <Th>Estado</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredPayments.map((p) => {
                const attrs = p.attributes || p;
                const student = attrs.student?.data?.attributes || attrs.student;
                const studentUser = student?.user?.data?.attributes || student?.user;
                const statusInfo = STATUS_MAP[attrs.status] || STATUS_MAP.paid;

                return (
                  <Tr key={p.id} _hover={{ bg: 'gray.50' }}>
                    <Td fontSize="sm">
                      {attrs.payment_date ? dayjs(attrs.payment_date).format('DD/MM/YYYY') : '-'}
                    </Td>
                    <Td fontSize="sm" fontWeight="medium">
                      {studentUser?.first_name || ''} {studentUser?.last_name || ''}
                    </Td>
                    <Td isNumeric fontWeight="semibold">
                      ${Number(attrs.amount || 0).toLocaleString()}
                    </Td>
                    <Td fontSize="sm" color="gray.600">{attrs.payment_method || '-'}</Td>
                    <Td>
                      <Text fontSize="sm" noOfLines={1} maxW="200px" color="gray.600">
                        {attrs.description || '-'}
                      </Text>
                    </Td>
                    <Td>
                      <Badge colorScheme={statusInfo.color} borderRadius="full" px={2}>
                        {statusInfo.label}
                      </Badge>
                    </Td>
                  </Tr>
                );
              })}
              {filteredPayments.length === 0 && (
                <Tr>
                  <Td colSpan={6} textAlign="center" py={8} color="gray.400">
                    {search || filterStatus
                      ? 'No se encontraron pagos con los filtros aplicados'
                      : 'No hay pagos registrados'}
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      {/* Modal: Registrar Pago */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalHeader>Registrar pago</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column" gap={4}>
              <FormControl isRequired>
                <FormLabel>Estudiante</FormLabel>
                <Select
                  placeholder="Seleccionar estudiante"
                  value={newPayment.student}
                  onChange={(e) => setNewPayment((p) => ({ ...p, student: e.target.value }))}
                >
                  {students.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.email})
                    </option>
                  ))}
                </Select>
              </FormControl>

              <Flex gap={4}>
                <FormControl isRequired flex="1">
                  <FormLabel>Monto</FormLabel>
                  <Input
                    type="number"
                    value={newPayment.amount}
                    onChange={(e) => setNewPayment((p) => ({ ...p, amount: e.target.value }))}
                    placeholder="0"
                  />
                </FormControl>
                <FormControl isRequired flex="1">
                  <FormLabel>Fecha de pago</FormLabel>
                  <Input
                    type="date"
                    value={newPayment.payment_date}
                    onChange={(e) => setNewPayment((p) => ({ ...p, payment_date: e.target.value }))}
                  />
                </FormControl>
              </Flex>

              <Flex gap={4}>
                <FormControl isRequired flex="1">
                  <FormLabel>Metodo de pago</FormLabel>
                  <Select
                    placeholder="Seleccionar metodo"
                    value={newPayment.payment_method}
                    onChange={(e) => setNewPayment((p) => ({ ...p, payment_method: e.target.value }))}
                  >
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl flex="1">
                  <FormLabel>Estado</FormLabel>
                  <Select
                    value={newPayment.status}
                    onChange={(e) => setNewPayment((p) => ({ ...p, status: e.target.value }))}
                  >
                    <option value="paid">Pagado</option>
                    <option value="pending">Pendiente</option>
                  </Select>
                </FormControl>
              </Flex>

              <FormControl>
                <FormLabel>Descripcion</FormLabel>
                <Textarea
                  value={newPayment.description}
                  onChange={(e) => setNewPayment((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Concepto del pago, mes, observaciones..."
                  rows={2}
                />
              </FormControl>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancelar
            </Button>
            <Button colorScheme="purple" isDisabled>
              Registrar pago
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
